import { useState } from "react";
import { Link } from "react-router-dom";
import { Withheld } from "./Withheld";

const SURFACE_TABS = [
  { id: "portfolio", label: "Portfolio" },
  { id: "memos", label: "Deal memos" },
  { id: "documents", label: "Documents" },
  { id: "transfers", label: "Transfers" },
];

const PORTFOLIO_ROWS = [
  ["11ch", "Common", "12,400", "4.10", "6.55", "1.60", "2 of 3 tranches"],
  ["9ch", "Common", "3,075", "18.00", "18.00", "1.00", "none"],
  ["10ch", "Preferred B", "840", "62.25", "71.80", "1.15", "1 of 3 tranches"],
];

const MEMO_CONTENTS = [
  "The company and its round history",
  "Price against the last round and observable marks",
  "Who is selling and why",
  "Path to liquidity",
  "What we do not know",
];

const DOCUMENT_ROWS = [
  ["Subscription agreement, ████████ ████", "Subscription", "2025-08-04"],
  ["Schedule K-1, tax year 2025", "Tax", "2026-03-14"],
  ["Member report, Q1 2026", "Report", "2026-04-09"],
];

export default function MemberSurfacePreview() {
  const [tab, setTab] = useState("portfolio");
  return (
    <section className="hairline py-16 rise" data-testid="platform-features">
      <div className="flex items-baseline justify-between gap-6 flex-wrap">
        <div>
          <span className="t-label">The member surface</span>
          <p className="t-body text-bone mt-4 measure">
            Members see what is theirs and what is on offer. Nothing else, and nowhere
            public. Below is the surface itself, with the names withheld.
          </p>
        </div>
        <Link to="/login" className="t-label hover:!text-[#E8E6E1]" data-testid="surface-login-link">Members enter here</Link>
      </div>

      <div className="mt-10 border border-[#24272B]" style={{ background: "#14171A" }} data-testid="member-surface-preview">
        <div className="flex items-center justify-between border-b border-[#24272B] px-6">
          <div className="flex gap-7 flex-wrap">
            {SURFACE_TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`t-label py-4 border-b transition-colors duration-150 ${tab === t.id ? "border-[#E8E6E1] !text-[#E8E6E1]" : "border-transparent hover:!text-[#E8E6E1]"}`}
                data-testid={`surface-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>
          <span className="t-cap text-muted-s fig hidden md:inline">preview // names withheld</span>
        </div>

        <div className="p-6 md:p-8 min-h-[300px]">
          {tab === "portfolio" && (
            <div data-testid="surface-panel-portfolio">
              <div className="grid grid-cols-3 gap-8 max-w-lg">
                {[["Positions", "3"], ["Value at mark", "USD 196,882"], ["MOIC", "1.28"]].map(([k, v]) => (
                  <div key={k}><span className="t-label">{k}</span><div className="fig text-bone text-xl mt-2">{v}</div></div>
                ))}
              </div>
              <table className="table-solera mt-8">
                <thead><tr><th>Position</th><th>Class</th><th className="num">Units</th><th className="num">Entry</th><th className="num">Mark</th><th className="num">MOIC</th><th>Distributions</th></tr></thead>
                <tbody>
                  {PORTFOLIO_ROWS.map((r) => (
                    <tr key={r[0]}>
                      <td><Withheld w={r[0]} /></td>
                      <td className="text-muted-s">{r[1]}</td>
                      <td className="num text-bone">{r[2]}</td>
                      <td className="num text-bone">{r[3]}</td>
                      <td className="num text-bone">{r[4]}</td>
                      <td className="num text-bone">{r[5]}</td>
                      <td className="text-muted-s t-small">{r[6]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="t-cap text-muted-s mt-4">Marks state their basis. Distributions are shown as tranches with dates, never as a single realized number.</p>
            </div>
          )}
          {tab === "memos" && (
            <div className="grid md:grid-cols-[1fr_280px] gap-10" data-testid="surface-panel-memos">
              <div className="paper-surface p-6 md:p-8">
                <div className="flex items-center justify-between">
                  <span className="wordmark text-xs" style={{ color: "#0E1012" }}>Solera</span>
                  <span className="t-label-paper">memo SOL-00██</span>
                </div>
                <div className="hairline-paper mt-4 pt-5">
                  <div className="text-2xl" style={{ fontWeight: 500 }}>████████ ██████</div>
                  <p className="t-small muted-p mt-1">Series D secondary. As at 1 June 2026.</p>
                </div>
                <div className="mt-5 space-y-2">
                  {MEMO_CONTENTS.map((h, i) => (
                    <div key={h} className="hairline-paper pt-2 first:border-t-0 first:pt-0 flex gap-4 items-baseline">
                      <span className="t-label-paper">{String(i + 1).padStart(2, "0")}</span>
                      <span className="t-small" style={{ color: "#0E1012" }}>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="t-label">Every memo, same contents</span>
                <p className="t-small text-muted-s mt-3">
                  The last section is never omitted. If a growth figure is a rumor, the memo
                  says it is a rumor. Commit against a stated minimum. Allocation is first
                  come, with stated exceptions. No countdowns, no progress bars, no urgency.
                </p>
              </div>
            </div>
          )}
          {tab === "documents" && (
            <div data-testid="surface-panel-documents">
              <table className="table-solera">
                <thead><tr><th>Document</th><th>Category</th><th>Date</th><th></th></tr></thead>
                <tbody>
                  {DOCUMENT_ROWS.map((r) => (
                    <tr key={r[0]}>
                      <td className="text-bone">{r[0]}</td>
                      <td className="text-muted-s">{r[1]}</td>
                      <td className="text-muted-s t-small fig">{r[2]}</td>
                      <td className="text-right"><span className="t-label">Members only</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="t-cap text-muted-s mt-4">Each file is issued to one member and is downloadable only by that member.</p>
            </div>
          )}
          {tab === "transfers" && (
            <div data-testid="surface-panel-transfers">
              <table className="table-solera">
                <thead><tr><th>Position</th><th>Class</th><th className="num">Units offered</th><th className="num">Asking</th><th></th></tr></thead>
                <tbody>
                  <tr>
                    <td><Withheld w="13ch" /></td>
                    <td className="text-muted-s">Common</td>
                    <td className="num text-bone">1,500</td>
                    <td className="num text-bone">14.75</td>
                    <td className="text-right"><span className="t-label">Members only</span></td>
                  </tr>
                  <tr>
                    <td><Withheld w="9ch" /></td>
                    <td className="text-muted-s">Preferred A</td>
                    <td className="num text-bone">620</td>
                    <td className="num text-bone">On request</td>
                    <td className="text-right"><span className="t-label">Members only</span></td>
                  </tr>
                </tbody>
              </table>
              <p className="t-small text-muted-s mt-4 measure">
                Members may offer part of a position to other members. Sellers are never
                named, interest is not binding, and Solera facilitates every transfer
                directly, with issuer confirmation before close.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
